import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ContentCopy as ContentCopyIcon } from "@mui/icons-material";
import axios from "axios";
import FlagIcon from "./FlagIcon";
import {
  NationsLeagueMatch,
  NationsLeagueMatchDay,
  NationsLeagueTeam,
} from "../types";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

interface Props {
  open: boolean;
  onClose: () => void;
  nationsLeagueId: string;
  teams: NationsLeagueTeam[];
  matches: NationsLeagueMatch[];
  onMatchesChanged?: () => void | Promise<void>;
}

// Match day = local calendar date of the match (YYYY-MM-DD)
const toDateKey = (value: Date | string): string => {
  const d = new Date(value);
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const rosterNames = (team?: NationsLeagueTeam): string[] =>
  (team?.players || []).map((p) => (typeof p === "string" ? p : p.name));

const NationsLeagueMatchDays: React.FC<Props> = ({
  open,
  onClose,
  nationsLeagueId,
  teams,
  matches,
  onMatchesChanged,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [records, setRecords] = useState<Record<string, NationsLeagueMatchDay>>({});
  const [error, setError] = useState("");
  const [copiedDate, setCopiedDate] = useState<string | null>(null);

  const base = `${API_URL}/api/nations-league/${nationsLeagueId}/match-days`;
  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
  });

  useEffect(() => {
    if (!open) return;
    setError("");
    axios
      .get<NationsLeagueMatchDay[]>(base, authHeaders())
      .then((res) => {
        const byDate: Record<string, NationsLeagueMatchDay> = {};
        res.data.forEach((r) => (byDate[r.date] = r));
        setRecords(byDate);
      })
      .catch((err) =>
        setError(err.response?.data?.error || "Failed to load match days"),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nationsLeagueId]);

  const teamsById = useMemo(() => {
    const map: Record<string, NationsLeagueTeam> = {};
    teams.forEach((t) => (map[t.id] = t));
    return map;
  }, [teams]);

  // date -> ids of teams playing that day
  const days = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    matches.forEach((m) => {
      if (!m.matchDateTime) return;
      const key = toDateKey(m.matchDateTime);
      (map[key] ||= new Set()).add(m.team1Id);
      map[key].add(m.team2Id);
    });
    return Object.keys(map)
      .sort()
      .map((date) => ({ date, teamIds: Array.from(map[date]) }));
  }, [matches]);

  const save = async (request: Promise<{ data: NationsLeagueMatchDay }>) => {
    try {
      setError("");
      const res = await request;
      setRecords((prev) => ({ ...prev, [res.data.date]: res.data }));
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to save");
    }
  };

  const setPaid = (date: string, teamId: string, paid: boolean) =>
    save(axios.put(`${base}/${date}/payments/${teamId}`, { paid }, authHeaders()));

  // Fill the team's side of that day's matches from its available players:
  // roster-ranked pairs go to Match 1, Match 2, Match 3. Only full pairs are filled, and
  // matches that already have a finished individual match are left alone.
  const pairsFor = (teamId: string, players: string[]): [string, string][] => {
    const roster = rosterNames(teamsById[teamId]);
    const ranked = [...players].sort((a, b) => roster.indexOf(a) - roster.indexOf(b));
    const pairs: [string, string][] = [];
    for (let i = 0; i + 1 < ranked.length && pairs.length < 3; i += 2) {
      pairs.push([ranked[i], ranked[i + 1]]);
    }
    return pairs;
  };

  // availability: teamId -> available players. All teams of a match are applied in one save.
  const fillMatchesFromAvailability = async (
    date: string,
    availability: Record<string, string[]>,
  ) => {
    const dayMatches = matches.filter(
      (m) =>
        m.matchDateTime &&
        toDateKey(m.matchDateTime) === date &&
        !m.matches?.some((im) => im.isCompleted),
    );

    const requests = dayMatches.flatMap((m) => {
      const pairs1 = pairsFor(m.team1Id, availability[m.team1Id] || []);
      const pairs2 = pairsFor(m.team2Id, availability[m.team2Id] || []);
      if (pairs1.length === 0 && pairs2.length === 0) return [];
      const payload = [0, 1, 2].map((i) => {
        const existing: any = m.matches?.[i] || {};
        const pair1 = pairs1[i];
        const pair2 = pairs2[i];
        return {
          matchNumber: i + 1,
          team1Player1: existing.team1Player1 || null,
          team1Player2: existing.team1Player2 || null,
          team2Player1: existing.team2Player1 || null,
          team2Player2: existing.team2Player2 || null,
          team1DidNotCompete: !!existing.team1DidNotCompete,
          team2DidNotCompete: !!existing.team2DidNotCompete,
          ...(i === 2 && existing.isSuperTiebreakDecider !== undefined && {
            isSuperTiebreakDecider: existing.isSuperTiebreakDecider,
          }),
          ...(pair1 && { team1Player1: pair1[0], team1Player2: pair1[1], team1DidNotCompete: false }),
          ...(pair2 && { team2Player1: pair2[0], team2Player2: pair2[1], team2DidNotCompete: false }),
        };
      });
      return [
        axios.put(
          `${API_URL}/api/nations-league/${nationsLeagueId}/matches/${m.id}/players`,
          { matches: payload },
          authHeaders(),
        ),
      ];
    });
    await Promise.all(requests);
    await onMatchesChanged?.();
  };

  const setAvailability = async (date: string, teamId: string, players: string[]) => {
    await save(
      axios.put(`${base}/${date}/availability/${teamId}`, { players }, authHeaders()),
    );
    try {
      // Include the other teams' saved availability so a match isn't left half-filled
      await fillMatchesFromAvailability(date, {
        ...records[date]?.availability,
        [teamId]: players,
      });
    } catch (err: any) {
      setError(err.response?.data?.error || "Saved availability, but failed to fill the matches");
    }
  };

  const fillDay = async (date: string) => {
    try {
      setError("");
      await fillMatchesFromAvailability(date, records[date]?.availability || {});
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to fill the matches");
    }
  };

  // WhatsApp-ready status list for the captains' group (*bold* is WhatsApp markup)
  const copyForWhatsApp = async (date: string, teamIds: string[]) => {
    const record = records[date];
    const title = new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
    });
    const lines = teamIds
      .map((id) => teamsById[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((team) => {
        const provided = (record?.availability?.[team.id]?.length || 0) > 0;
        const paid = !!record?.payments?.[team.id]?.paid;
        return `*${team.name}*\nPlayers: ${provided ? "✅" : "❌ still needed"}\nPower ranking: ${team.rosterLocked ? "✅" : "❌ still needed"}\nPaid:${paid ? "✅" : "❌ still needed"}`;
      });
    const text = `*Match day – ${title}*\n\n${lines.join("\n\n")}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedDate(date);
      setTimeout(() => setCopiedDate((d) => (d === date ? null : d)), 2000);
    } catch {
      setError("Couldn't copy to the clipboard");
    }
  };

  const setRevealed = (date: string, revealed: boolean) =>
    save(
      axios.put(
        `${base}/${date}/reveal`,
        { revealed, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone },
        authHeaders(),
      ),
    );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Match Days</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {days.length === 0 && (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No matches have a date yet. Set match dates first; each date becomes a match day.
          </Typography>
        )}
        {days.map(({ date, teamIds }) => {
          const record = records[date];
          const paidCount = teamIds.filter((id) => record?.payments?.[id]?.paid).length;
          const revealed = !!record?.availabilityRevealed;
          return (
            <Paper key={date} variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  {new Date(`${date}T00:00:00`).toLocaleDateString("en-GB", {
                    weekday: "long",
                    day: "numeric",
                    month: "long",
                  })}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Chip
                    size="small"
                    color={paidCount === teamIds.length ? "success" : "default"}
                    label={`${paidCount}/${teamIds.length} paid`}
                  />
                  <Button size="small" variant="outlined" onClick={() => fillDay(date)}>
                    Fill matches
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => copyForWhatsApp(date, teamIds)}
                  >
                    {copiedDate === date ? "Copied!" : "Copy for WhatsApp"}
                  </Button>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={revealed}
                        onChange={(e) => setRevealed(date, e.target.checked)}
                      />
                    }
                    label={revealed ? "Match players shown to public" : "Match players hidden"}
                  />
                </Box>
              </Box>

              {teamIds.map((teamId) => {
                const team = teamsById[teamId];
                if (!team) return null;
                const paid = !!record?.payments?.[teamId]?.paid;
                return (
                  <Box
                    key={teamId}
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      alignItems: { xs: "stretch", sm: "center" },
                      gap: 1.5,
                      py: 1,
                      borderTop: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 180 }}>
                      <FlagIcon flag={team.flag} />
                      <Typography>{team.name}</Typography>
                    </Box>
                    <Chip
                      clickable
                      size="small"
                      color={paid ? "success" : "error"}
                      variant={paid ? "filled" : "outlined"}
                      label={paid ? "Paid" : "Not paid"}
                      onClick={() => setPaid(date, teamId, !paid)}
                    />
                    <Autocomplete
                      multiple
                      size="small"
                      sx={{ flex: 1 }}
                      options={rosterNames(team)}
                      value={record?.availability?.[teamId] || []}
                      onChange={(_, value) => setAvailability(date, teamId, value)}
                      renderInput={(params) => (
                        <TextField {...params} label="Available players" />
                      )}
                    />
                  </Box>
                );
              })}
            </Paper>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default NationsLeagueMatchDays;
